import { type FC } from "react";

import { Anchor, Tabs, VisuallyHidden } from "@mantine/core";
import { ExternalLinkIcon, Sizes } from "@snapchat/snap-design-system-icons";
import { clsx } from "clsx";

import { LENS_PLUS_TOP_CHARTS_URL } from "../../shared/constants/externalLinks";
import { type useLensPlusMonth } from "../hooks/useLensPlusMonth";
import { type LensPlusPayoutHistory } from "../hooks/useLensPlusPayoutHistory";
import { MonetizationTab, isMonetizationTab } from "../models";

import { externalTab, tabs, tabsRow } from "./MonetizationTabs.module.css";
import { LensPlusTab } from "./lens-plus/LensPlusTab";
import { OpensInNewTab, TabLensPlusPayouts, TabLensPlusTopCharts, TabTopPerformerPayouts } from "./messages.generated";
import { TopPerformerTab } from "./top-performer";

interface MonetizationTabsProps {
    tab: MonetizationTab;
    selection: ReturnType<typeof useLensPlusMonth>;
    history: LensPlusPayoutHistory;
    onChange: (tab: MonetizationTab) => void;
}

const { Tab, List, Panel } = Tabs;

export const MonetizationTabs: FC<MonetizationTabsProps> = ({ tab, onChange, selection, history }) => (
    <Tabs
        value={tab}
        onChange={(value) => {
            if (isMonetizationTab(value)) onChange(value);
        }}
        className={tabs}
        keepMounted={false}
    >
        <div className={tabsRow}>
            <List>
                <Tab value={MonetizationTab.LensPlus} className="subheading-sh300">
                    <TabLensPlusPayouts />
                </Tab>
                <Tab value={MonetizationTab.TopPerformer} className="subheading-sh300">
                    <TabTopPerformerPayouts />
                </Tab>
            </List>
            <Anchor
                href={LENS_PLUS_TOP_CHARTS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={clsx(externalTab, "subheading-sh300")}
                underline="never"
            >
                <TabLensPlusTopCharts /> <ExternalLinkIcon size={Sizes.Small} aria-hidden="true" />
                <VisuallyHidden>
                    <OpensInNewTab />
                </VisuallyHidden>
            </Anchor>
        </div>

        <Panel value={MonetizationTab.LensPlus}>
            <div data-testid="monetization-lens-plus-panel">
                <LensPlusTab selection={selection} history={history} />
            </div>
        </Panel>
        <Panel value={MonetizationTab.TopPerformer}>
            <div data-testid="monetization-top-performer-panel">
                <TopPerformerTab />
            </div>
        </Panel>
    </Tabs>
);
